import { type GeoJSONFeature, LngLat, type MapMouseEvent, type PaddingOptions, Point, Popup } from 'mapbox-gl';
import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { Map, type MapboxMap, type ViewStateChangeEvent } from 'react-map-gl';
import { first } from 'lodash';
import { Text } from '@chakra-ui/react';
import { point, featureCollection, bbox, bboxPolygon, booleanPointInPolygon } from '@turf/turf';
import {
  showActivityMarkerTooltip,
  showQuiverPlotTooltip,
  showTidalDiamondTooltip,
  showVesselTooltip,
  showVesselTrackTooltip,
} from './utils/tooltip-utils';
import {
  operationAutoSwitchMinZoomLevel,
  sidePanelMapCenterOffsetPixels,
  timelineMapCenterOffsetPixels,
} from './constants/constants';
import {
  getActiveLayerIds,
  innerCircleLayer,
  operationLayer,
  outerCircleBorderLayer,
  outerCircleLayer,
  quiverPlotLayer,
  tidalDiamondLayer,
  vesselShapeLayer,
  vesselShapeLayer3D,
} from './constants/layers-config';
import {
  type ActivityMarkerFeatureProperties,
  type FeatureSearchArea,
  type HoverHandlerContext,
  type HoverHandlerTypedContext,
  MapLayer,
  type OperationViewModel,
  type TidalDiamondFeatureProperties,
  type TooltipRef,
  type TooltipProperties,
  type VesselFeatureProperties,
  type VesselTrackFeatureProperties,
} from './types';
import { hasLayer, hideLayer, orderLayers, showLayer } from './utils/layer-utils';
import {
  getAvailableVesselLayers,
  getAvailableVesselTrackMarkerLayers,
  getClosestQuiverPlotArrow,
} from './utils/map-utils';
import { type MapOrchestrator } from './hooks/useMapOrchestration';
import {
  type AnalyticsEvent,
  CONFIG_CODES,
  Event,
  type ITimer,
  type Location,
  Mode,
  Page,
  type TargetVessel,
  type VesselPosition,
} from '../../types';
import { getConfig } from '../../utils/get-config';
import { getEventFactory } from '../../utils/get-event-factory';
import { useAppInsights } from '../../hooks/useAppInsights';
import { type Timestamp } from '../../services/Timestamp';
import { Timer } from '../../services/Timer';
import { useMapContext, useTripsContext } from '../GlobalContextProvider/GlobalContextProvider';
import { translate } from '../../constants/translation';
import { useDetectMobileScreenWidth } from '../../hooks/useDetectMobileScreenWidth';
import { useAppContext } from '../AppContextProvider/AppContextProvider';
import { secretNames } from '../../constants/constants';
import { useFetchSecret } from '../../hooks/queries/useFetchSecret';

const MAPBOX_DEFAULT_STYLE = getConfig(CONFIG_CODES.REACT_APP_MAPBOX_STYLE);
const MAPBOX_SATELLITE_STYLE = getConfig(CONFIG_CODES.REACT_APP_MAPBOX_SATELLITE_STYLE);
const MAPBOX_NAUTICAL_STYLE = getConfig(CONFIG_CODES.REACT_APP_MAPBOX_NAUTICAL_STYLE);
const MAX_ARROW_DISTANCE_FROM_POINT = Number(getConfig(CONFIG_CODES.MAX_ARROW_DISTANCE_FROM_POINT_IN_PIXELS));
const minTooltipEventDurationInSeconds = 2;

interface Props {
  children: ReactNode;
  vesselPositions: VesselPosition[];
  initialMapCenter: Location;
  shouldDisableKeyboard?: boolean;
  setLockedOnTarget: Dispatch<SetStateAction<boolean>>;
  onTargetVesselChange: (mmsi: string, vesselName: string) => void;
  targetVessel: TargetVessel;
  targetVesselPosition: VesselPosition;
  timestamp: Timestamp;
  page: Page;
  operation: OperationViewModel;
  onOperationSelect: (value: string) => void;
  isWorldView: boolean;
  isTripsPanelOpen: boolean;
  pageMode: Mode;
  mapOrchestrator: MapOrchestrator;
  setActiveTidalDiamond: (tidalDiamondId: string) => void;
  timezone: string;
}

const MapComponent = ({
  children,
  vesselPositions,
  initialMapCenter,
  shouldDisableKeyboard = false,
  setLockedOnTarget,
  onTargetVesselChange,
  targetVessel,
  targetVesselPosition,
  timestamp,
  page,
  operation,
  onOperationSelect,
  isWorldView,
  isTripsPanelOpen,
  pageMode,
  mapOrchestrator,
  setActiveTidalDiamond,
  timezone,
}: Props): JSX.Element => {
  const isMobileScreenWidth = useDetectMobileScreenWidth();
  const { settings } = useMapContext();
  const { mode } = useAppContext();
  const mapToken = useFetchSecret(secretNames.mapboxToken);
  const {
    map: mapRef,
    onTransitionToLiveview,
    onTransitionToPlayback,
    onMapZoomAdjustment,
    getInitialZoomLevel,
    onMapCenter,
    onVesselRecenter,
  } = mapOrchestrator;

  const { operations } = useTripsContext();
  const hasVesselPositions = useMemo(() => {
    return vesselPositions?.length > 0;
  }, [vesselPositions]);

  const configs: HoverHandlerContext[] = [
    {
      getSearchArea: e => e.point,
      getSearchableLayers: () => [operationLayer.id],
      order: 1,
    } as HoverHandlerContext,
    {
      getSearchArea: e => e.point,
      getSearchableLayers: map => (hasVesselPositions ? getAvailableVesselLayers(map) : []),
      order: 2,
      openTooltipHandler: (event, map, properties) => {
        openVesselTooltip(properties, map, event.point);
      },
    } as HoverHandlerTypedContext<VesselFeatureProperties>,
    {
      getSearchArea: e => e.point,
      getSearchableLayers: map =>
        hasVesselPositions &&
        hasLayer(map, outerCircleBorderLayer.id) &&
        hasLayer(map, outerCircleLayer.id) &&
        hasLayer(map, innerCircleLayer.id)
          ? [outerCircleBorderLayer.id, outerCircleLayer.id, innerCircleLayer.id]
          : [],
      order: 3,
      openTooltipHandler: (event, map, properties) => {
        openActivityMarkerTooltip(properties, map);
      },
    } as HoverHandlerTypedContext<ActivityMarkerFeatureProperties>,
    {
      getSearchArea: e => e.point,
      getSearchableLayers: map => (hasVesselPositions ? getAvailableVesselTrackMarkerLayers(map) : []),
      order: 4,
      openTooltipHandler: (event, map, properties) => {
        openVesselTrackTooltip(properties, map);
      },
    } as HoverHandlerTypedContext<VesselTrackFeatureProperties>,
    {
      getSearchArea: e => e.point,
      getSearchableLayers: map => (hasLayer(map, tidalDiamondLayer.id) ? [tidalDiamondLayer.id] : []),
      order: 5,
      openTooltipHandler: (event, map, properties) => {
        openTooltip(
          t =>
            getEventFactory(Event.TidalDiamondHovered)(
              page,
              properties,
              t.current.timer.elapsed as Timestamp,
              window.location.href,
              timestamp
            ),
          () => {
            showTidalDiamondTooltip(properties, tooltipRef, map);
            setActiveTidalDiamond(properties.id);
          }
        );
      },
      noFeaturesFoundHandler: () => {
        setActiveTidalDiamond(null);
      },
    } as HoverHandlerTypedContext<TidalDiamondFeatureProperties>,
  ];

  const paddingOptions = useMemo(() => {
    return {
      right: isTripsPanelOpen ? sidePanelMapCenterOffsetPixels : 0,
      bottom: page === Page.Playback ? timelineMapCenterOffsetPixels : 0,
    } as PaddingOptions;
  }, [isTripsPanelOpen, page]);

  useEffect(() => {
    if (pageMode === Mode.Playback) {
      return;
    }

    onTransitionToLiveview(isWorldView, targetVessel?.mmsi, shouldRecenter() ? getCenter() : null, paddingOptions);
  }, [isWorldView, isMobileScreenWidth, pageMode]);

  useEffect(() => {
    if (pageMode !== Mode.Playback) {
      return;
    }

    onTransitionToPlayback(
      isWorldView,
      vesselPositions,
      targetVessel?.mmsi,
      shouldRecenter() ? getCenter() : null,
      paddingOptions
    );
  }, [isWorldView, isMobileScreenWidth, pageMode, vesselPositions]);

  useEffect(() => {
    if (operation) {
      const { coordinates } = operation.location;
      centerMapTo({
        lat: coordinates.lat,
        lng: coordinates.lon,
      } as LngLat);
    } else {
      const { coordinates } = initialMapCenter;
      centerMapTo({
        lat: coordinates.lat,
        lng: coordinates.lon,
      } as LngLat);
      const globalZoom = getInitialZoomLevel(undefined, true);
      onMapZoomAdjustment(globalZoom, undefined, null);
    }
  }, [operation?.key]);

  const mapCenterRef = useRef({
    lng: initialMapCenter?.coordinates?.lon,
    lat: initialMapCenter?.coordinates?.lat,
  } as LngLat);
  const tooltipRef = useRef<TooltipRef>({ popup: new Popup(), timer: new Timer() as ITimer } as TooltipRef);

  const appInsights = useAppInsights();

  const [isPitched, setIsPitched] = useState(false);
  const { mmsi: targetVesselMmsi, isLockedOnTarget } = targetVessel;

  const handleZoom = (event: ViewStateChangeEvent): void => {
    onMapZoomAdjustment(event.viewState.zoom, page, targetVessel?.mmsi);
  };

  const handlePan = (): void => {
    if (isLockedOnTarget) {
      setLockedOnTarget(false);
    }
  };

  const getOperationsInViewport = (): OperationViewModel[] => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { _ne, _sw } = mapRef.current.getMap().getBounds();
    const ne = point(_ne.toArray());
    const sw = point(_sw.toArray());
    const collection = featureCollection([ne, sw]);
    const box = bbox(collection);
    const square = bboxPolygon(box);
    return (operations ?? []).filter(o =>
      booleanPointInPolygon(new LngLat(o.location.coordinates.lon, o.location.coordinates.lat).toArray(), square)
    );
  };

  const switchOperationIfNeeded = (): void => {
    const zoom = mapRef.current?.getMap()?.getZoom();
    if (!zoom || zoom < operationAutoSwitchMinZoomLevel) {
      return;
    }

    const viewportOperations = getOperationsInViewport();

    if (viewportOperations.length !== 1) {
      return;
    }

    const viewportOperation = first(viewportOperations);
    if (viewportOperation.key === operation?.key) {
      return;
    }

    onOperationSelect(viewportOperation.key);
  };

  const handlePanEnd = (): void => {
    const newCenter = mapRef.current.getMap().getCenter();
    const event = getEventFactory(Event.PanMap)(
      page,
      window.location.href,
      targetVesselMmsi,
      timestamp,
      mapCenterRef.current,
      newCenter
    );
    appInsights.trackAnalyticsEvent(event);
    mapCenterRef.current = newCenter;
    switchOperationIfNeeded();
  };

  const handleMouseMove = (event: MapMouseEvent): void => {
    const map = mapRef?.current?.getMap();
    if (!map) {
      return;
    }

    disableKeyboardIfNeeded(map);
    mapRef.current.getCanvas().style.cursor = '';

    // eslint-disable-next-line no-restricted-syntax
    for (const config of configs) {
      const layers = config.getSearchableLayers(map);
      if (!layers.length) {
        continue;
      }

      const searchArea = config.getSearchArea(event);
      const features = getFeatures(searchArea, layers);

      if ((features?.length ?? 0) === 0) {
        config.noFeaturesFoundHandler?.();
        continue;
      }

      handleHover(event, map, features, config);
      return;
    }

    if (!isQuiverPlotTooltipVisible()) {
      closeTooltip();
    }
  };

  const isQuiverPlotTooltipVisible = (): boolean => {
    return tooltipRef.current.popup.getElement()?.className.includes('quiver-plot-tooltip');
  };

  const getFeatures = (searchArea: FeatureSearchArea, layers: string[]): GeoJSONFeature[] => {
    return mapRef.current.queryRenderedFeatures(searchArea, { layers });
  };

  const disableKeyboardIfNeeded = (map: MapboxMap): void => {
    if (shouldDisableKeyboard && map?.keyboard?.isEnabled()) {
      map.keyboard.disable();
    }
  };

  const handleHover = <TProperties extends TooltipProperties>(
    event: MapMouseEvent,
    map: MapboxMap,
    features: GeoJSONFeature[],
    config: HoverHandlerTypedContext<TProperties>
  ): void => {
    mapRef.current.getCanvas().style.cursor = 'pointer';
    const feature = first(features);
    const properties = feature.properties as TProperties;

    if (tooltipRef.current.popup.isOpen()) {
      if (tooltipRef.current.popup.getElement().id === properties.tooltipId) {
        return;
      }
      closeTooltip();
    }

    config.openTooltipHandler?.(event, map, properties);
  };

  const openTooltip = (
    eventOnCloseFactory: (tooltip: React.MutableRefObject<TooltipRef>) => AnalyticsEvent,
    showTooltipLogic: () => void
  ): void => {
    showTooltipLogic();
    tooltipRef.current.eventOnCloseFactory = eventOnCloseFactory;
    tooltipRef.current.timer.start();
  };

  const openVesselTooltip = (properties: VesselFeatureProperties, map: mapboxgl.Map, point: Point): void => {
    openTooltip(
      t =>
        getEventFactory(Event.VesselHovered)(
          page,
          properties,
          t.current.timer.elapsed as Timestamp,
          window.location.href,
          timestamp
        ),
      () => {
        showVesselTooltip(properties, tooltipRef, map, point);
      }
    );
  };

  const openVesselTrackTooltip = (trackProperties: VesselTrackFeatureProperties, map: mapboxgl.Map): void => {
    openTooltip(
      () =>
        getEventFactory(Event.VesselTrackHovered)(
          page,
          operation?.name,
          timestamp,
          trackProperties.speed,
          trackProperties.mmsi
        ),
      () => {
        showVesselTrackTooltip(trackProperties, tooltipRef, map, timezone);
      }
    );
  };

  const openActivityMarkerTooltip = (properties: ActivityMarkerFeatureProperties, map: mapboxgl.Map): void => {
    openTooltip(
      () =>
        getEventFactory(Event.ActivityMarkerHovered)(
          page,
          operation?.name,
          timestamp,
          properties.speed,
          properties.mmsi
        ),
      () => {
        showActivityMarkerTooltip(properties, tooltipRef, map, timezone);
      }
    );
  };

  const closeTooltip = (): void => {
    tooltipRef.current.popup.remove();
    tooltipRef.current.timer.stop();

    if (tooltipRef.current.timer.elapsed.toSeconds() >= minTooltipEventDurationInSeconds) {
      appInsights.trackAnalyticsEvent(tooltipRef.current.eventOnCloseFactory(tooltipRef));
    }
    tooltipRef.current.timer.reset();
  };

  const handleClick = (event: MapMouseEvent): void => {
    const map = mapRef?.current?.getMap();
    if (hasLayer(map, operationLayer.id)) {
      const operationFeatures = mapRef.current.queryRenderedFeatures(event.point, {
        layers: [operationLayer.id],
      });
      if (operationFeatures.length > 0) {
        onOperationSelect(first(operationFeatures).properties.key);
      }
    }

    if (vesselPositions.length > 0 && map) {
      const features = getFeatures(event.point, getAvailableVesselLayers(map));
      if (features.length > 0) {
        const feature = first(features);
        const { mmsi, name } = feature.properties;
        onTargetVesselChange(mmsi, name);
      }
    }

    if (canDisplayQuiverPlotTooltip(map, event)) {
      displayQuiverPlotTooltip(map, event);
    }
  };

  const displayQuiverPlotTooltip = (map: MapboxMap, event: MapMouseEvent): void => {
    const quiverPlotArrows = getFeatures(getQuiverPlotArrowsSearchBox(event.point), [quiverPlotLayer.id]);

    if (!quiverPlotArrows.length) {
      return;
    }

    const closestArrow = getClosestQuiverPlotArrow(quiverPlotArrows, event);
    const aiEvent = getEventFactory(Event.TidalArrowClicked)(page, closestArrow, window.location.href, timestamp);
    appInsights.trackAnalyticsEvent(aiEvent);
    showQuiverPlotTooltip(closestArrow, tooltipRef, map);
  };

  const getQuiverPlotArrowsSearchBox = (clickedPoint: Point): [Point, Point] => {
    const { x, y } = clickedPoint;
    return [
      new Point(x - MAX_ARROW_DISTANCE_FROM_POINT, y - MAX_ARROW_DISTANCE_FROM_POINT),
      new Point(x + MAX_ARROW_DISTANCE_FROM_POINT, y + MAX_ARROW_DISTANCE_FROM_POINT),
    ];
  };

  const canDisplayQuiverPlotTooltip = (map: MapboxMap, event: MapMouseEvent): boolean => {
    if (!hasLayer(map, quiverPlotLayer.id)) {
      return false;
    }

    const allFeatures = getFeatures(
      event.point,
      getActiveLayerIds(settings).filter(l => l !== quiverPlotLayer.id)
    );
    return !allFeatures.length;
  };

  const handlePitchChange = (event: ViewStateChangeEvent): void => {
    const { pitch } = event.viewState;
    const map = mapRef.current.getMap();
    if (isEntering3DView(pitch)) {
      enable3DView(map);
      setIsPitched(true);
    } else if (isExiting3DView(pitch)) {
      exit3DView(map);
      setIsPitched(false);
    }
  };

  const isExiting3DView = (pitch: number): boolean => {
    return pitch === 0 && isPitched;
  };

  const isEntering3DView = (pitch: number): boolean => {
    return pitch > 0 && !isPitched;
  };

  const switchLayersFor3d = (map: MapboxMap): void => {
    showLayer(map, vesselShapeLayer3D.id);
    hideLayer(map, vesselShapeLayer.id);
  };

  const switchLayersFor2d = (map: MapboxMap): void => {
    showLayer(map, vesselShapeLayer.id);
    hideLayer(map, vesselShapeLayer3D.id);
  };

  const exit3DView = (map: MapboxMap): void => {
    switchLayersFor2d(map);
    const event = getEventFactory(Event.Exited3dView)(page, window.location.href, targetVesselMmsi, timestamp);
    appInsights.trackAnalyticsEvent(event);
  };

  const enable3DView = (map: MapboxMap): void => {
    switchLayersFor3d(map);
    const event = getEventFactory(Event.Entered3dView)(page, window.location.href, targetVesselMmsi, timestamp);
    appInsights.trackAnalyticsEvent(event);
  };

  const getCenter = (): LngLat => {
    const coordinates = targetVesselPosition?.location?.coordinates;
    if (coordinates) {
      return {
        lat: coordinates.lat,
        lng: coordinates.lon,
      } as LngLat;
    }
  };

  const recenterMapOnTargetVessel = (): void => {
    const center = getCenter();
    onVesselRecenter(page, targetVesselMmsi, center, paddingOptions);
    mapCenterRef.current = center;
  };

  const centerMapTo = (center: LngLat): void => {
    onMapCenter(center, paddingOptions);
    mapCenterRef.current = center;
  };

  const centerMap = (): void => {
    const center = getCenter();
    centerMapTo(center);
  };

  const shouldRecenter = (): boolean => {
    return isLockedOnTarget && targetVesselPosition && !!mapRef.current;
  };

  useEffect(() => {
    if (shouldRecenter()) {
      recenterMapOnTargetVessel();
    }
  }, [isLockedOnTarget, targetVesselMmsi, targetVesselPosition, mapRef.current]);

  // Center map on targeted vessel when any change in dependency array occurs
  useEffect(() => {
    if (shouldRecenter()) {
      centerMap();
    }
  }, [targetVesselPosition, mapRef.current, isTripsPanelOpen]);

  const mapStyles = {
    [MapLayer.Default]: MAPBOX_DEFAULT_STYLE,
    [MapLayer.Satellite]: MAPBOX_SATELLITE_STYLE,
    [MapLayer.Nautical]: MAPBOX_NAUTICAL_STYLE,
  };

  const mapStyle = mapStyles[settings?.layer] || MAPBOX_DEFAULT_STYLE;

  useEffect(() => {
    const map = mapRef?.current?.getMap();
    if (!map) {
      return;
    }

    if (!map.isStyleLoaded()) {
      return;
    }

    if (map.getStyle()?.sprite !== mapStyle) {
      map.setStyle(mapStyle);
    }
  }, [mapRef?.current, settings?.layer]);

  const handleStyleData = (): void => {
    const map = mapRef?.current?.getMap();

    if (!map) {
      return;
    }

    const pitch = map.getPitch();
    if (pitch > 0) {
      switchLayersFor3d(map);
    } else {
      switchLayersFor2d(map);
    }
  };

  useEffect(() => {
    const map = mapRef?.current?.getMap();

    if (!map) {
      return;
    }
    orderLayers(map);
  }, [settings?.showVesselNames, settings?.showVesselTracks, targetVessel?.mmsi, mapRef?.current]);

  return (
    <div className="map-container">
      {initialMapCenter && mapToken && (
        <Map
          initialViewState={{
            latitude: initialMapCenter.coordinates.lat,
            longitude: initialMapCenter.coordinates.lon,
            zoom: getInitialZoomLevel(mode, isWorldView),
          }}
          onZoom={handleZoom}
          onZoomEnd={switchOperationIfNeeded}
          onDrag={handlePan}
          onDragEnd={handlePanEnd}
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          onRotate={handlePitchChange}
          mapboxAccessToken={mapToken}
          mapStyle={mapStyle}
          projection={{ name: 'globe' }}
          ref={mapRef}
          onStyleData={handleStyleData}
        >
          {children}
          {isWorldView && (
            <Text className="select-operation-message text-xxl-regular">{translate('SELECT_YOUR_OPERATION')}</Text>
          )}
        </Map>
      )}
    </div>
  );
};

export default MapComponent;
